import React from 'react';
import { _ } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { getColor } from '../../../helpers';

/**
 * Hook that generates color mappings for clean series names.
 * Extracts clean series names from allData using the FULL period data.
 * This hook simply assigns colors to each unique series name.
 *
 * @param localData - Local data containing clean series keys (already normalized, without period prefixes)
 * @param xAxisField - The field name used for the x-axis (to exclude from series)
 * @param seriesColorByName - Optional custom color mapping that will override defaults
 * @returns An object containing:
 *   - seriesColorByName: Record mapping clean series names to their color hex values
 *   - seriesKeys: Array of clean series names (excluding xAxisField)
 */
export const useNormalizedSeriesAndColors = ({
  localData,
  xAxisField,
  seriesColorByName,
}: {
  localData: types.widgets.TableData | null;
  xAxisField: string;
  seriesColorByName?: Record<string, string>;
}) => {
  return React.useMemo(() => {
    const keys = _.without(Object.keys(localData?.[0] ?? {}), xAxisField);
    const defaultColors: Record<string, string> = {};

    keys.forEach((key, index) => {
      defaultColors[key] = getColor(index);
    });

    const finalSeriesColorByName = { ...defaultColors, ...(seriesColorByName ?? {}) };

    return {
      seriesColorByName: finalSeriesColorByName,
      seriesKeys: keys,
    };
  }, [localData, xAxisField, seriesColorByName]);
};
